import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Filter, ArrowLeft, RefreshCw, PackageX } from "lucide-react";

import { getFilteredOrders } from "../../../api/integrations";
import { showToast } from "../../../utils/showToast";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Button } from "../../../components/ui/Button";

// Razones estables del motor de reglas -> texto legible para el operador.
const REASON_LABELS = {
  "coverage-comuna-not-covered": "Comuna fuera de la cobertura seleccionada",
  "coverage-comuna-unresolved": "Comuna del pedido no reconocida",
  "rule-not-matched": "No cumple las reglas configuradas",
  "rule-blocked": "Bloqueado por una regla",
  "data-unavailable-hold": "Datos de producto no disponibles (retenido)",
  "data-unavailable-skip": "Datos de producto no disponibles",
};
const reasonLabel = (r) => REASON_LABELS[r] || r || "—";

const OUTCOME_LABELS = { skip: "No creado", hold: "Retenido" };

const PAGE_SIZE = 20;

export default function FilteredOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await getFilteredOrders({ page: p, limit: PAGE_SIZE });
      if (res && res.error) {
        showToast(t("Error al obtener los pedidos filtrados"), false);
        setItems([]);
        setTotal(0);
      } else if (res && res.ok) {
        setItems(res.items || []);
        setTotal(res.total || 0);
      }
    } catch (err) {
      console.error("getFilteredOrders error:", err);
      showToast(t("Error al obtener los pedidos filtrados"), false);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(page);
  }, [page, load]);

  const fmtDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString("es-CL");
    } catch {
      return String(d);
    }
  };

  const fmtMoney = (snapshot) => {
    if (snapshot?.total === undefined || snapshot?.total === null) return "—";
    const cur = snapshot.currency ? `${snapshot.currency} ` : "";
    return `${cur}${Number(snapshot.total).toLocaleString("es-CL")}`;
  };

  return (
    <div className="w-full min-w-0">
      <PageHeader
        icon={<Filter className="w-6 h-6 text-fg-muted" />}
        title={t("Pedidos filtrados")}
        subtitle={t("Pedidos que tus reglas de creación no enviaron a Beartrack, con el motivo.")}
      />

      <div className="flex items-center gap-2 mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/home/integrations")}
          iconLeft={<ArrowLeft size={15} aria-hidden="true" />}
        >
          {t("Volver a integraciones")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(page)}
          iconLeft={<RefreshCw size={15} aria-hidden="true" />}
        >
          {t("Actualizar")}
        </Button>
        <span className="text-sm text-fg-muted ml-auto">
          {total} {t("pedido{{s}} filtrado{{s}}", { s: total !== 1 ? "s" : "" })}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-fg-muted py-8 text-center">{t("Cargando…")}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-fg-muted">
          <PackageX size={28} aria-hidden="true" />
          <p className="text-sm">{t("No hay pedidos filtrados.")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-3 py-2 font-medium">{t("Fecha")}</th>
                <th className="px-3 py-2 font-medium">{t("Tienda")}</th>
                <th className="px-3 py-2 font-medium">{t("Pedido")}</th>
                <th className="px-3 py-2 font-medium">{t("Destinatario")}</th>
                <th className="px-3 py-2 font-medium">{t("Comuna")}</th>
                <th className="px-3 py-2 font-medium">{t("Total")}</th>
                <th className="px-3 py-2 font-medium">{t("Motivo")}</th>
                <th className="px-3 py-2 font-medium">{t("Resultado")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it._id} className="border-b border-border/60 last:border-0">
                  <td className="px-3 py-2 whitespace-nowrap text-fg-muted">{fmtDate(it.updatedAt)}</td>
                  <td className="px-3 py-2">{it.storeAlias || it.integrationName}</td>
                  <td className="px-3 py-2 font-mono">{it.orderId}</td>
                  <td className="px-3 py-2">{it.snapshot?.receiverName || "—"}</td>
                  <td className="px-3 py-2">{it.snapshot?.comuna || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtMoney(it.snapshot)}</td>
                  <td className="px-3 py-2">{t(reasonLabel(it.reason))}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block text-xs px-2 py-0.5 rounded ${
                        it.outcome === "hold"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-bg-subtle text-fg-muted"
                      }`}
                    >
                      {t(OUTCOME_LABELS[it.outcome] || it.outcome)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t("Anterior")}
          </Button>
          <span className="text-sm text-fg-muted">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            {t("Siguiente")}
          </Button>
        </div>
      )}
    </div>
  );
}
